/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { HalJson } from '../models/hal-json';
import { MenuHoursAssociationsBulkResult } from '../models/menu-hours-associations-bulk-result';
import { MenuHoursGroup } from '../models/menu-hours-group';
import { menuHoursGroupAssociationsBulkDelete } from '../fn/menu-hours/menu-hours-group-associations-bulk-delete';
import { MenuHoursGroupAssociationsBulkDelete$Params } from '../fn/menu-hours/menu-hours-group-associations-bulk-delete';
import { menuHoursGroupAssociationsBulkPost } from '../fn/menu-hours/menu-hours-group-associations-bulk-post';
import { MenuHoursGroupAssociationsBulkPost$Params } from '../fn/menu-hours/menu-hours-group-associations-bulk-post';
import { menuHoursGroupAssociationsGet } from '../fn/menu-hours/menu-hours-group-associations-get';
import { MenuHoursGroupAssociationsGet$Params } from '../fn/menu-hours/menu-hours-group-associations-get';
import { MenuHoursGroupAssociationsResponse } from '../models/menu-hours-group-associations-response';
import { menuHoursGroupDefaultPut } from '../fn/menu-hours/menu-hours-group-default-put';
import { MenuHoursGroupDefaultPut$Params } from '../fn/menu-hours/menu-hours-group-default-put';
import { menuHoursGroupDelete } from '../fn/menu-hours/menu-hours-group-delete';
import { MenuHoursGroupDelete$Params } from '../fn/menu-hours/menu-hours-group-delete';
import { menuHoursGroupGet } from '../fn/menu-hours/menu-hours-group-get';
import { MenuHoursGroupGet$Params } from '../fn/menu-hours/menu-hours-group-get';
import { menuHoursGroupPost } from '../fn/menu-hours/menu-hours-group-post';
import { MenuHoursGroupPost$Params } from '../fn/menu-hours/menu-hours-group-post';
import { menuHoursGroupPut } from '../fn/menu-hours/menu-hours-group-put';
import { MenuHoursGroupPut$Params } from '../fn/menu-hours/menu-hours-group-put';
import { MenuHoursGroupResult } from '../models/menu-hours-group-result';
import { menuHoursGroupsGet } from '../fn/menu-hours/menu-hours-groups-get';
import { MenuHoursGroupsGet$Params } from '../fn/menu-hours/menu-hours-groups-get';
import { MenuHoursGroupsResponse } from '../models/menu-hours-groups-response';

@Injectable({ providedIn: 'root' })
export class MenuHoursService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `menuHoursGroupsGet()` */
  static readonly MenuHoursGroupsGetPath = '/menus/{menuId}/hours';

  /**
   * Get reusable hours groups for a menu.
   *
   * Returns reusable hours groups for the menu and explicit warnings when the same store is assigned to multiple groups for the same aggregator.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `menuHoursGroupsGet()` instead.
   *
   * This method doesn't expect any request body.
   */
  menuHoursGroupsGet$Response(params: MenuHoursGroupsGet$Params, context?: HttpContext): Observable<StrictHttpResponse<MenuHoursGroupsResponse>> {
    return menuHoursGroupsGet(this.http, this.rootUrl, params, context);
  }

  /**
   * Get reusable hours groups for a menu.
   *
   * Returns reusable hours groups for the menu and explicit warnings when the same store is assigned to multiple groups for the same aggregator.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `menuHoursGroupsGet$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  menuHoursGroupsGet(params: MenuHoursGroupsGet$Params, context?: HttpContext): Observable<MenuHoursGroupsResponse> {
    return this.menuHoursGroupsGet$Response(params, context).pipe(
      map((r: StrictHttpResponse<MenuHoursGroupsResponse>): MenuHoursGroupsResponse => r.body)
    );
  }

  /** Path part for operation `menuHoursGroupPost()` */
  static readonly MenuHoursGroupPostPath = '/menus/{menuId}/hours';

  /**
   * Create a reusable hours group for a menu.
   *
   * Creates a reusable menu hours group.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `menuHoursGroupPost()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  menuHoursGroupPost$Response(params: MenuHoursGroupPost$Params, context?: HttpContext): Observable<StrictHttpResponse<{
'_links'?: HalJson;
} & MenuHoursGroup & {

/**
 * Indicates whether this group should be treated as the menu's brand-level hours group.
 */
'isBrandMenuHours'?: boolean;
}>> {
    return menuHoursGroupPost(this.http, this.rootUrl, params, context);
  }

  /**
   * Create a reusable hours group for a menu.
   *
   * Creates a reusable menu hours group.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `menuHoursGroupPost$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  menuHoursGroupPost(params: MenuHoursGroupPost$Params, context?: HttpContext): Observable<{
'_links'?: HalJson;
} & MenuHoursGroup & {

/**
 * Indicates whether this group should be treated as the menu's brand-level hours group.
 */
'isBrandMenuHours'?: boolean;
}> {
    return this.menuHoursGroupPost$Response(params, context).pipe(
      map((r: StrictHttpResponse<{
'_links'?: HalJson;
} & MenuHoursGroup & {

/**
 * Indicates whether this group should be treated as the menu's brand-level hours group.
 */
'isBrandMenuHours'?: boolean;
}>): {
'_links'?: HalJson;
} & MenuHoursGroup & {

/**
 * Indicates whether this group should be treated as the menu's brand-level hours group.
 */
'isBrandMenuHours'?: boolean;
} => r.body)
    );
  }

  /** Path part for operation `menuHoursGroupGet()` */
  static readonly MenuHoursGroupGetPath = '/menus/{menuId}/hours/{hoursId}';

  /**
   * Get a reusable hours group for a menu.
   *
   * Returns a single reusable menu hours group together with warnings related to conflicting store and aggregator assignments.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `menuHoursGroupGet()` instead.
   *
   * This method doesn't expect any request body.
   */
  menuHoursGroupGet$Response(params: MenuHoursGroupGet$Params, context?: HttpContext): Observable<StrictHttpResponse<MenuHoursGroupResult>> {
    return menuHoursGroupGet(this.http, this.rootUrl, params, context);
  }

  /**
   * Get a reusable hours group for a menu.
   *
   * Returns a single reusable menu hours group together with warnings related to conflicting store and aggregator assignments.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `menuHoursGroupGet$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  menuHoursGroupGet(params: MenuHoursGroupGet$Params, context?: HttpContext): Observable<MenuHoursGroupResult> {
    return this.menuHoursGroupGet$Response(params, context).pipe(
      map((r: StrictHttpResponse<MenuHoursGroupResult>): MenuHoursGroupResult => r.body)
    );
  }

  /** Path part for operation `menuHoursGroupPut()` */
  static readonly MenuHoursGroupPutPath = '/menus/{menuId}/hours/{hoursId}';

  /**
   * Update a reusable hours group for a menu.
   *
   * Replaces a reusable menu hours group and returns explicit warnings for any overlapping store and aggregator assignments.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `menuHoursGroupPut()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  menuHoursGroupPut$Response(params: MenuHoursGroupPut$Params, context?: HttpContext): Observable<StrictHttpResponse<MenuHoursGroupResult>> {
    return menuHoursGroupPut(this.http, this.rootUrl, params, context);
  }

  /**
   * Update a reusable hours group for a menu.
   *
   * Replaces a reusable menu hours group and returns explicit warnings for any overlapping store and aggregator assignments.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `menuHoursGroupPut$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  menuHoursGroupPut(params: MenuHoursGroupPut$Params, context?: HttpContext): Observable<MenuHoursGroupResult> {
    return this.menuHoursGroupPut$Response(params, context).pipe(
      map((r: StrictHttpResponse<MenuHoursGroupResult>): MenuHoursGroupResult => r.body)
    );
  }

  /** Path part for operation `menuHoursGroupDelete()` */
  static readonly MenuHoursGroupDeletePath = '/menus/{menuId}/hours/{hoursId}';

  /**
   * Delete a reusable hours group for a menu.
   *
   * Deletes a reusable menu hours group.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `menuHoursGroupDelete()` instead.
   *
   * This method doesn't expect any request body.
   */
  menuHoursGroupDelete$Response(params: MenuHoursGroupDelete$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return menuHoursGroupDelete(this.http, this.rootUrl, params, context);
  }

  /**
   * Delete a reusable hours group for a menu.
   *
   * Deletes a reusable menu hours group.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `menuHoursGroupDelete$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  menuHoursGroupDelete(params: MenuHoursGroupDelete$Params, context?: HttpContext): Observable<void> {
    return this.menuHoursGroupDelete$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `menuHoursGroupDefaultPut()` */
  static readonly MenuHoursGroupDefaultPutPath = '/menus/{menuId}/hours/{hoursId}/brand-menu';

  /**
   * Set a hours group as the menu's default.
   *
   * Sets a reusable hours group as the default (brand-level) hours group for a menu.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `menuHoursGroupDefaultPut()` instead.
   *
   * This method doesn't expect any request body.
   */
  menuHoursGroupDefaultPut$Response(params: MenuHoursGroupDefaultPut$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return menuHoursGroupDefaultPut(this.http, this.rootUrl, params, context);
  }

  /**
   * Set a hours group as the menu's default.
   *
   * Sets a reusable hours group as the default (brand-level) hours group for a menu.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `menuHoursGroupDefaultPut$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  menuHoursGroupDefaultPut(params: MenuHoursGroupDefaultPut$Params, context?: HttpContext): Observable<void> {
    return this.menuHoursGroupDefaultPut$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `menuHoursGroupAssociationsGet()` */
  static readonly MenuHoursGroupAssociationsGetPath = '/menus/{menuId}/hours/{hoursId}/associations';

  /**
   * Get store and aggregator associations for a hours group.
   *
   * Returns all store and aggregator associations for a specific reusable hours group.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `menuHoursGroupAssociationsGet()` instead.
   *
   * This method doesn't expect any request body.
   */
  menuHoursGroupAssociationsGet$Response(params: MenuHoursGroupAssociationsGet$Params, context?: HttpContext): Observable<StrictHttpResponse<MenuHoursGroupAssociationsResponse>> {
    return menuHoursGroupAssociationsGet(this.http, this.rootUrl, params, context);
  }

  /**
   * Get store and aggregator associations for a hours group.
   *
   * Returns all store and aggregator associations for a specific reusable hours group.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `menuHoursGroupAssociationsGet$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  menuHoursGroupAssociationsGet(params: MenuHoursGroupAssociationsGet$Params, context?: HttpContext): Observable<MenuHoursGroupAssociationsResponse> {
    return this.menuHoursGroupAssociationsGet$Response(params, context).pipe(
      map((r: StrictHttpResponse<MenuHoursGroupAssociationsResponse>): MenuHoursGroupAssociationsResponse => r.body)
    );
  }

  /** Path part for operation `menuHoursGroupAssociationsBulkPost()` */
  static readonly MenuHoursGroupAssociationsBulkPostPath = '/menus/{menuId}/hours/{hoursId}/associations/bulk';

  /**
   * Bulk assign stores and aggregators to a hours group.
   *
   * Bulk assigns stores and aggregators to a reusable hours group.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `menuHoursGroupAssociationsBulkPost()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  menuHoursGroupAssociationsBulkPost$Response(params: MenuHoursGroupAssociationsBulkPost$Params, context?: HttpContext): Observable<StrictHttpResponse<MenuHoursAssociationsBulkResult>> {
    return menuHoursGroupAssociationsBulkPost(this.http, this.rootUrl, params, context);
  }

  /**
   * Bulk assign stores and aggregators to a hours group.
   *
   * Bulk assigns stores and aggregators to a reusable hours group.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `menuHoursGroupAssociationsBulkPost$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  menuHoursGroupAssociationsBulkPost(params: MenuHoursGroupAssociationsBulkPost$Params, context?: HttpContext): Observable<MenuHoursAssociationsBulkResult> {
    return this.menuHoursGroupAssociationsBulkPost$Response(params, context).pipe(
      map((r: StrictHttpResponse<MenuHoursAssociationsBulkResult>): MenuHoursAssociationsBulkResult => r.body)
    );
  }

  /** Path part for operation `menuHoursGroupAssociationsBulkDelete()` */
  static readonly MenuHoursGroupAssociationsBulkDeletePath = '/menus/{menuId}/hours/{hoursId}/associations/bulk-delete';

  /**
   * Bulk remove stores and aggregators from a hours group.
   *
   * Bulk removes store and aggregator associations from a reusable hours group.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `menuHoursGroupAssociationsBulkDelete()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  menuHoursGroupAssociationsBulkDelete$Response(params: MenuHoursGroupAssociationsBulkDelete$Params, context?: HttpContext): Observable<StrictHttpResponse<MenuHoursAssociationsBulkResult>> {
    return menuHoursGroupAssociationsBulkDelete(this.http, this.rootUrl, params, context);
  }

  /**
   * Bulk remove stores and aggregators from a hours group.
   *
   * Bulk removes store and aggregator associations from a reusable hours group.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `menuHoursGroupAssociationsBulkDelete$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  menuHoursGroupAssociationsBulkDelete(params: MenuHoursGroupAssociationsBulkDelete$Params, context?: HttpContext): Observable<MenuHoursAssociationsBulkResult> {
    return this.menuHoursGroupAssociationsBulkDelete$Response(params, context).pipe(
      map((r: StrictHttpResponse<MenuHoursAssociationsBulkResult>): MenuHoursAssociationsBulkResult => r.body)
    );
  }

}
